import styled from "styled-components";
import { black, gray, grayDark, white } from "../styles/colors";
import { ScrollReveal } from "./ScrollReveal";

const USE_CASES = [
  {
    emoji: "🏋️",
    title: "Fitness",
    description: "Track form, reps, and consistency as your body changes week by week.",
  },
  {
    emoji: "🎸",
    title: "Music",
    description: "Film daily practice and hear yourself improve — even when it feels slow.",
  },
  {
    emoji: "💼",
    title: "Career",
    description: "Document side projects, interviews, and skills you're building on the side.",
  },
  {
    emoji: "🎨",
    title: "Creative work",
    description: "Capture sketches, drafts, and iterations from rough idea to finished piece.",
  },
  {
    emoji: "📚",
    title: "Learning",
    description: "Record study sessions, language practice, or anything you're mastering.",
  },
  {
    emoji: "🌱",
    title: "Life changes",
    description: "Moving cities, sobriety, parenting — the journeys that matter most.",
  },
];

export function UseCases() {
  return (
    <Section>
      <Header>
        <ScrollReveal>
          <Eyebrow>Built for real pursuits</Eyebrow>
          <Title>Any journey worth taking</Title>
          <Subtitle>
            If you care enough to show up every day, Progress gives you a place
            to prove it — to yourself and the people cheering you on.
          </Subtitle>
        </ScrollReveal>
      </Header>
      <Grid>
        {USE_CASES.map((item, index) => (
          <ScrollReveal key={item.title} delay={(index % 3) as 0 | 1 | 2}>
            <Card>
              <Emoji aria-hidden="true">{item.emoji}</Emoji>
              <CardTitle>{item.title}</CardTitle>
              <CardText>{item.description}</CardText>
            </Card>
          </ScrollReveal>
        ))}
      </Grid>
    </Section>
  );
}

const Section = styled.section({
  padding: "96px 24px",
  background: white,
});

const Header = styled.div({
  maxWidth: 680,
  margin: "0 auto 56px",
  textAlign: "center",
});

const Eyebrow = styled.p({
  margin: "0 0 12px",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: gray,
});

const Title = styled.h2({
  margin: "0 0 16px",
  fontSize: "clamp(32px, 4vw, 44px)",
  fontWeight: 700,
  letterSpacing: "-0.03em",
  color: black,
});

const Subtitle = styled.p({
  margin: 0,
  fontSize: 18,
  lineHeight: 1.7,
  color: grayDark,
});

const Grid = styled.div({
  maxWidth: 1100,
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 20,

  "@media (max-width: 900px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },

  "@media (max-width: 560px)": {
    gridTemplateColumns: "1fr",
  },
});

const Card = styled.div({
  height: "100%",
  padding: "28px 24px",
  borderRadius: 20,
  border: "1px solid rgba(35, 35, 35, 0.08)",
  background: white,
  transition: "transform 0.2s ease, box-shadow 0.2s ease",

  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 16px 40px rgba(35, 35, 35, 0.08)",
  },
});

const Emoji = styled.div({
  fontSize: 28,
  marginBottom: 16,
});

const CardTitle = styled.h3({
  margin: "0 0 8px",
  fontSize: 20,
  fontWeight: 700,
  color: black,
});

const CardText = styled.p({
  margin: 0,
  fontSize: 15,
  lineHeight: 1.65,
  color: grayDark,
});
